import React, { useEffect, useState } from 'react';
import palettes from '../data/palettes.json';
import fonts from '../data/fonts.json';

const StylesLeftPredefined = ({ userChoices, setUserChoices, onSaveAndContinue }) => {
  const [selectedTheme, setSelectedTheme] = useState(userChoices.theme);
  const [filteredPalettes, setFilteredPalettes] = useState([]);
  const [showAllPalettes, setShowAllPalettes] = useState(false);

  // Set filtered palettes when theme changes
  useEffect(() => {
    setFilteredPalettes(palettes[selectedTheme]);
  }, [selectedTheme]);

  const handleDeviceChange = (device) => {
    setUserChoices((prev) => ({ ...prev, device }));
  };

  const handleThemeChange = (theme) => {
    setSelectedTheme(theme);
    const defaultPalette = palettes[theme][0].name;
    setUserChoices((prev) => ({ 
      ...prev, 
      theme, 
      palette: defaultPalette,
      isCustomPalette: false,
      customPaletteId: null
    }));
  };

  const handlePaletteChange = (palette) => {
    setUserChoices((prev) => ({ 
      ...prev, 
      palette,
      isCustomPalette: false,
      customPaletteId: null
    }));
  };

  const handleFontChange = (event) => {
    const font = event.target.value;
    setUserChoices((prev) => ({ ...prev, font }));
  };

  // Determine which palettes to display based on showAllPalettes state
  const displayedPalettes = showAllPalettes ? filteredPalettes : filteredPalettes.slice(0, 3);

  return (
    <div className="h-full p-4 overflow-y-auto">
      {/* Device and Font Selection */}
      <div className="flex flex-wrap md:flex-nowrap items-center mb-6 w-full max-w-xl mx-auto">
        {/* Device Selection */}
        <div className="w-full md:w-1/2 mb-4 md:mb-0">
          <h2 className="text-lg font-semibold mb-2">Design for:</h2>
          <div className="flex space-x-4">
            <button
              onClick={() => handleDeviceChange('Desktop')}
              className={`px-4 py-2 rounded-md ${
                userChoices.device === 'Desktop'
                  ? 'bg-black text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              Desktop
            </button>
            <button
              onClick={() => handleDeviceChange('Mobile')}
              className={`px-4 py-2 rounded-md ${
                userChoices.device === 'Mobile'
                  ? 'bg-black text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              Mobile
            </button>
          </div>
        </div>

        {/* Font Family Selection */}
        <div className="w-full md:w-1/2 md:pl-2">
          <h2 className="text-lg font-semibold mb-2">Use Font:</h2>
          <select
            value={userChoices.font}
            onChange={handleFontChange}
            className="w-full px-4 py-2 pr-6 rounded-md bg-gray-200 text-gray-700"
            style={{
              fontFamily: fonts.find((font) => font.name === userChoices.font)?.family,
            }}
          >
            {fonts.map((font) => (
              <option
                key={font.name}
                value={font.name}
                style={{ fontFamily: font.family }}
              >
                {font.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Theme and Palette Selection */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2">Use Theme:</h2>
        <div className="flex space-x-4 mb-4">
          <button
            onClick={() => handleThemeChange('light')}
            className={`px-4 py-2 rounded-md ${
              selectedTheme === 'light' ? 'bg-black text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            Light
          </button>
          <button
            onClick={() => handleThemeChange('dark')}
            className={`px-4 py-2 rounded-md ${
              selectedTheme === 'dark' ? 'bg-black text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            Dark
          </button>
        </div>

        <div className="space-y-2">
          {displayedPalettes.map((palette) => {
            const isSelected = userChoices.palette === palette.name && !userChoices.isCustomPalette;
            const isDarkTheme = selectedTheme === 'dark';
            const borderClass = isSelected
              ? isDarkTheme
                ? 'border-2 border-pink-500' // Lighter border for selected in dark theme
                : 'border-2 border-black' // Selected in light theme
              : isDarkTheme
                ? 'border border-gray-700' // Darker border for unselected in dark theme
                : 'border border-gray-300'; // Unselected in light theme
            const textColor = isDarkTheme ? 'text-white' : 'text-black';
            const bgColor = isDarkTheme ? 'bg-gray-800' : 'bg-white';
            return (
              <div
                key={palette.name}
                className={`flex items-center p-2 rounded-md cursor-pointer ${borderClass} ${bgColor} ${textColor}`}
                onClick={() => handlePaletteChange(palette.name)}
              >
                <div className="flex space-x-2 mr-4">
                  <div className="w-6 h-6 rounded" style={{ backgroundColor: palette.primary }}></div>
                  <div className="w-6 h-6 rounded" style={{ backgroundColor: palette.secondary }}></div>
                  <div className="w-6 h-6 rounded" style={{ backgroundColor: palette.accent }}></div>
                </div>
                <span>{palette.name}</span>
              </div>
            );
          })}
          {!showAllPalettes && filteredPalettes.length > 3 && (
            <button
              onClick={() => setShowAllPalettes(true)}
              className="mt-2 text-blue-500 underline"
            >
              Show more color palettes
            </button>
          )}
        </div>
      </div>

      <button
        onClick={onSaveAndContinue}
        className="w-full px-4 py-3 mt-2 mb-4 bg-black text-white rounded-md hover:bg-gray-800"
      >
        Save and Continue
      </button>
    </div>
  );
};

export default StylesLeftPredefined;